import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { SelectList } from '../../models/select-list';
import { BaseComponent } from '../component-base';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html'
})
export class DropdownComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() label: string = '';
  @Input() options: SelectList[] = [];
  @Input() formGroup?: FormGroup;
  @Input() controlName: string = '';
  @Input() isSubmitted: boolean = false;
  @Input() selectedValue: any;
  @Input() defaultSelectLabel: string = 'select';
  @Input() isRequired: boolean = true;
  @Input() addOptionalHint: boolean = false;
  @Input() showOptionalLabel: boolean = true;
  @Input() otherValue: string = '';
  @Input() otherPlaceholder: string = 'common.pleaseSpecify';

  @Output() selectionChange = new EventEmitter<any>();
  @Output() otherValueChange = new EventEmitter<string>();

  control?: FormControl;
  showOtherInput: boolean = false;
  otherInputControl = new FormControl('');
  private valueChangeSubscription?: Subscription;

  constructor() {
    super();
  }

  ngOnInit() {

    if (this.formGroup && this.controlName) {
      this.control = this.formGroup.get(this.controlName) as FormControl;
      // Always set the value - if null/undefined, set to empty string to match "--Select--" option
      // Convert to string to match select element's string values
      const valueToSet = (this.selectedValue !== undefined && this.selectedValue !== null) ? String(this.selectedValue) : '';
      this.control.setValue(valueToSet);

      // Subscribe to value changes to detect when form is patched with new data
      this.valueChangeSubscription = this.control.valueChanges.subscribe(() => {
        this.checkIfOtherSelected();
      });
    }
    this.checkIfOtherSelected();
    if (this.otherValue) {
      this.otherInputControl.setValue(this.otherValue);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.control && this.formGroup && this.controlName) {
      this.control = this.formGroup.get(this.controlName) as FormControl;
    }

    if (changes['selectedValue'] && this.control) {
      const newValue = changes['selectedValue'].currentValue;
      // Always set the value - if null/undefined, set to empty string to match "--Select--" option
      // Convert to string to match select element's string values
      const valueToSet = (newValue !== undefined && newValue !== null) ? String(newValue) : '';
      this.control.setValue(valueToSet);
    }

    // Re-apply the current value when options change (async load race condition fix)
    if (changes['options'] && this.control) {
      const currentValue = this.selectedValue ?? this.control.value;
      // Convert to string to match select element's string values
      const valueToSet = (currentValue !== null && currentValue !== undefined) ? String(currentValue) : '';
      setTimeout(() => {
        this.control?.setValue(valueToSet);
        this.checkIfOtherSelected();
      }, 0);
    }

    if (changes['otherValue']) {
      this.otherInputControl.setValue(this.otherValue || '');
    }
  }

  onSelectionChange(event: any) {
    if (this.control) {
      this.control.markAsTouched();
    }
    this.checkIfOtherSelected();
    this.selectionChange.emit(event);
  }

  private checkIfOtherSelected() {
    if (!this.control || !this.options) return;
    const selectedId = this.control.value;
    const selectedOption = this.options.find(opt => String(opt.id) === String(selectedId));
    const wasShowingOther = this.showOtherInput;
    this.showOtherInput = selectedOption?.isOther === true;

    // Reset other value if not selected anymore
    if (wasShowingOther && !this.showOtherInput) {
      this.otherInputControl.setValue('');
      this.otherValueChange.emit('');
    }
  }

  onOtherInputChange(value: string) {
    this.otherValueChange.emit(value);
    if (this.otherInputControl) {
      this.otherInputControl.markAsTouched();
    }
  }

  get isOtherRequired(): boolean {
    return this.isRequired && this.showOtherInput;
  }

  ngOnDestroy() {
    // Clean up subscription to prevent memory leaks
    this.valueChangeSubscription?.unsubscribe();
  }
}
